/**
 * Drawing Text Extractor & Query Tool
 * 
 * Uses backend /api/drawings-agent for secure Gemini access:
 * - Upload drawings via /api/upload-drawing-to-gemini (cached 48h)
 * - Query via /api/drawings-agent with streaming
 * - Return answers with structured citations
 */

// No direct Gemini client needed - all calls go through backend

// ============================================================
// TYPES
// ============================================================

export interface DrawingCitation {
  pageNumber: number;
  sheetName?: string;       // e.g., "E-101", "M-001"
  quote: string;            // Exact text that supports the answer
  context?: string;         // Surrounding context for clarity
  noteNumber?: string;      // If citing a specific note like "Note 3"
  analysis?: string;        // Reasoning - why this citation is relevant
}

export interface DrawingQueryResult {
  answer: string;
  citations: DrawingCitation[];
  confidence: 'high' | 'medium' | 'low';
  pages_analyzed: number;
  total_text_chars: number;
}

// ============================================================
// GET GEMINI FILE URI (via serverless function)
// ============================================================

/**
 * Get or create a Gemini Files API URI for the drawing
 * Calls the serverless function which handles:
 * - Caching (48h expiration)
 * - Compression if >50MB
 * - Upload to Gemini
 */
async function getGeminiFileUri(
  pdfUrl: string,
  estimationId: string,
  onProgress?: (thought: string) => void
): Promise<string> {
  onProgress?.('Preparing drawing for analysis...');

  const response = await fetch('/api/upload-drawing-to-gemini', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileUri: pdfUrl, estimationId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload drawing to Gemini');
  }

  const { geminiFileUri } = await response.json();
  onProgress?.('Drawing ready for analysis');
  
  return geminiFileUri;
}

// ============================================================
// QUERY WITH CITATIONS (via backend API)
// ============================================================

/**
 * Query the drawing with a question and return structured citations
 * Uses backend /api/drawings-agent for secure Gemini access
 */
async function queryWithCitations(
  geminiFileUri: string,
  question: string,
  onProgress?: (thought: string) => void
): Promise<DrawingQueryResult> {
  
  // Call backend API with streaming
  const response = await fetch('/api/drawings-agent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, geminiFileUri })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to query drawings');
  }

  // Handle SSE streaming response
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let result: DrawingQueryResult | null = null;
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    
    // Process complete SSE events
    const lines = buffer.split('\n\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      
      try {
        const event = JSON.parse(line.slice(6));
        
        switch (event.type) {
          case 'thought':
            console.log('💭 Drawing Thought:', event.data?.substring(0, 100) + '...');
            onProgress?.(event.data || '');
            break;
          case 'status':
            console.log('📊 Drawing Status:', event.data);
            onProgress?.(event.data || '');
            break;
          case 'result':
            result = event.data;
            break;
          case 'error':
            throw new Error(event.data);
          case 'done':
            console.log('✅ Drawing stream complete');
            break;
        }
      } catch (e) {
        if (line.includes('"type"')) {
          console.warn('Failed to parse SSE event:', line, e);
        }
      }
    }
  }

  if (!result) {
    throw new Error('No result received from drawings agent');
  }

  return result;
}

// ============================================================
// MAIN: Query Drawings
// ============================================================

/**
 * Main function: Query drawings with a question
 * 
 * 1. Upload drawing to Gemini (cached, with compression if needed)
 * 2. Query with structured citations via backend API
 */
export async function queryDrawings(
  pdfUrl: string,
  question: string,
  onProgress?: (thought: string) => void,
  startPage: number = 1,
  endPage?: number,
  estimationId?: string
): Promise<DrawingQueryResult> {
  
  if (!estimationId) {
    throw new Error('estimationId is required for drawing queries (used for caching)');
  }

  try {
    console.log(`📊 Drawing Query: "${question.substring(0, 50)}..."`);

    // Step 1: Get Gemini file URI (upload if needed, use cache if available)
    onProgress?.('Loading drawings...');
    
    const geminiFileUri = await getGeminiFileUri(pdfUrl, estimationId, onProgress);
    console.log('✅ Got Gemini file URI:', geminiFileUri);

    // Step 2: Query with citations via backend API
    onProgress?.('Analyzing drawing content...');
    
    console.log('💬 Querying drawings with citations...');
    
    const result = await queryWithCitations(geminiFileUri, question, onProgress);
    
    console.log(`\n✅ QUERY COMPLETE`);
    console.log(`   Citations: ${result.citations.length}`);
    console.log(`   Confidence: ${result.confidence}`);
    console.log(`   Pages analyzed: ${result.pages_analyzed}`);
    result.citations.forEach((c, i) => {
      console.log(`   [${i + 1}] Page ${c.pageNumber}${c.sheetName ? ` (${c.sheetName})` : ''}: "${c.quote.substring(0, 50)}..."`);
    });
    
    onProgress?.(`Found ${result.citations.length} references`);
    
    return result;
    
  } catch (error) {
    console.error('❌ Drawing query failed:', error);
    throw error;
  }
}
